import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useQuery } from '@apollo/client'
import { useParams } from 'react-router-dom'
import gql from 'graphql-tag'
import { BoardQuery } from './Queries'
import ListingFilters from '../Material/ListingFilters'
import { debounce } from 'lodash'
import '../Drivers/DriversNew/DriversListContainer.css'
import './TaskManagementListHeader.css'
import toTitleCase from '../Functions/toTitleCase'
import { Row, Col, Input, Button } from "reactstrap"
import NewTaskModal from "./TaskComponents/NewTaskModal"
import NewBoardModal from "./TaskComponents/NewBoardModal"
import withApolloClient from '../withApolloClient'
import { HasPermissionsQuery } from "../Functions/querys"
import { TaskStatusOptionsQuery } from './Queries'
import TableColumnPreferenceControl from "../Material/TableColumnPreferenceControl"

const RepsQuery = gql`
     query AllRepsQuery($groups:[String], $orderBy:[String]){
        optionsList: allReps(isStaff:true, isActive:true, groups:$groups, orderBy:$orderBy){
        edges{
          node{
            id
            name
            username
            firstName
            lastName
            email
            isBilling
            isFrontOffice
            assignedCount
          }
        }
      }
    }
`

const BoardsQuery = gql`
query AllBoards($orderBy:[String], $first: Int, $after: String){
    optionsList: allBoards(orderBy: $orderBy, first: $first, after: $after){
        edges{
            node{
                id
                title
            }
        }
    }
}`

function KanbanSettingsButton({ boardId, groupBy, onGroupByChange }) {
    const [showSettings, setShowSettings] = useState(false)
    const settingsRef = useRef(null)
    const { data } = useQuery(BoardQuery, { variables: { id: boardId }, skip: !boardId, fetchPolicy: 'cache-first' })
    const enumColumns = data?.board?.columnSet?.edges
        ?.filter(({ node }) => node.columnType === 'ENUM' && !node.isArchived)
        .map(({ node }) => node) || []

    useEffect(() => {
        if (!showSettings) return
        const handler = (e) => { if (settingsRef.current && !settingsRef.current.contains(e.target)) setShowSettings(false) }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [showSettings])

    return (
        <span ref={settingsRef} style={{ position: 'relative', display: 'inline-block', marginLeft: '4px', verticalAlign: 'middle' }}>
            <Button size="sm" color="secondary" className="mr-2" outline={!showSettings} onClick={() => setShowSettings(v => !v)} title="Configure kanban columns">
                <i className="fa fa-cog" />
            </Button>
            {showSettings && (
                <div className="kanban-settings-dropdown" style={{ right: 0, left: 'auto', top: 'calc(100% + 4px)', zIndex: 1050 }}>
                    <div className={`kanban-settings-option${groupBy === 'status' ? ' active' : ''}`} onClick={() => { onGroupByChange('status'); setShowSettings(false) }}>Status</div>
                    {enumColumns.map(col => (
                        <div key={col.pk} className={`kanban-settings-option${String(groupBy) === String(col.pk) ? ' active' : ''}`} onClick={() => { onGroupByChange(String(col.pk)); setShowSettings(false) }}>
                            {col.title}
                        </div>
                    ))}
                </div>
            )}
        </span>
    )
}

const TaskManagementListHeader = ({ activeTab, filterValues, setFilterValues, showNewBoardButton, parentBoardId, viewType, onViewTypeChange, groupBy, onGroupByChange, defaultColumnConfig, columnConfig, setColumnConfig, tableId, ...props }) => {
    const [openModal, setOpenModal] = useState("")
    const [configurations, setConfigurations] = useState(null)
    const safeFilterValues = filterValues || {}
    const isStatusKanbanView = viewType === "kanban" && groupBy === "status"

    let { boardId } = useParams()
    if (parentBoardId) {
        boardId = parentBoardId
    }

    const updateSearchTerm = useCallback(debounce((searchTerm) => { setFilterValues({ ...safeFilterValues, searchTerm }) }, 500), [safeFilterValues])

    const { data: hasPermissions, loading: hasPermissionsLoading } = useQuery(HasPermissionsQuery, {
        variables: {
            userPermissions: ["add_board", "add_taskrabbit"]
        }
    })

    const getFilterConfigurations = (activeTab) => {
        return [
            {
                type: "select", name: "statuses", title: "Status", valueSelector: "name", labelSelector: "description",
                optionsQuery: TaskStatusOptionsQuery, variables: [], placeholder: "Filter By Status", showFilter: !isStatusKanbanView, isMulti: true,
            },
            {
                type: "select", name: "assignedToIds", title: "Assigned Reps", valueSelector: "id", labelSelector: "name",
                optionsQuery: RepsQuery, variables: [], placeholder: "Filter By Assigned Reps",
                showFilter: true, isMulti: true,
            },
            {
                type: "select", name: "boardIds", title: "Boards", valueSelector: "id", labelSelector: "title",
                optionsQuery: BoardsQuery, variables: [], placeholder: "Filter By Boards",
                showFilter: !boardId, isMulti: true,
            },
            {
                type: "boolean", name: "isArchived", title: "Is Archived", optionOne: "YES", optionTwo: "NO", placeholder: "Filter By Is Archieved", showFilter: true, isMulti: true,
            },
        ]
    }
    useEffect(() => {
        const conf = getFilterConfigurations(activeTab)
        const fetchConfigurations = async () => {
            const configurations = await Promise.all(conf.filter(filter => filter.showFilter).map(async filter => {
                if (filter.optionsQuery) {
                    const result = await props.apolloClient.query({
                        query: filter.optionsQuery,
                        variables: { ...filter.variables }
                    })
                    let options = []
                    if (result && result.data.optionsList && result.data.optionsList.edges) {
                        options = result.data.optionsList.edges.map(options => options.node && ({
                            value: options.node[filter.valueSelector],
                            label: options.node[filter.labelSelector]
                        }))
                    } else if (result && result.data.optionsList && result.data.optionsList.enumValues) {
                        options = result.data.optionsList.enumValues.map(option => ({
                            value: option[filter.valueSelector],
                            label: option[filter.labelSelector] || option[filter.valueSelector]
                        }))
                    }
                    if (filter.name === "assignedToIds") {
                        options = [{ value: "UNASSIGNED", label: "Unassigned" }, ...options]
                    }
                    filter.options = options
                    delete filter.optionsQuery
                }
                return filter
            }))
            setConfigurations(configurations)
        }
        fetchConfigurations()
    }, [props.apolloClient, activeTab, boardId, isStatusKanbanView])

    if (!props.apolloClient) {
        return null
    }


    const toggleModal = (modalName) => {
        setOpenModal(openModal === modalName ? "" : modalName)
    }

    const removeFilter = (filterName) => {
        let newFilterValues = { ...safeFilterValues }
        if (newFilterValues.hasOwnProperty(filterName)) {
            delete newFilterValues[filterName]
            setFilterValues(newFilterValues)
        }
    }

    const getValueByFilterType = (filterConfigs, value) => {
        if (filterConfigs.type === "select") {
            let options = filterConfigs.options
            if (options && options.length > 0) {
                if (Array.isArray(value)) {
                    value = value.map(item => options.find(option => option.value === item).label)
                } else {
                    if (value === true) {
                        value = 'true'
                    } else if (value === false) {
                        value = 'false'
                    }
                    value = options.find(option => option.value === value) && options.find(option => option.value === value).label
                }
            }
        } else if (filterConfigs.type === "boolean") {
            value = toTitleCase(value.toString())
        } else if (filterConfigs.type === "date-range") {
            value = value[0].startDate && value[0].endDate ? value[0].startDate : ""
        }
        return Array.isArray(value) ? value.join(", ") : value
    }
    return (
        <Row>
            {openModal === "viewFilters" && (
                <ListingFilters open={openModal === "viewFilters"} handleClose={() => toggleModal("")}
                    target="viewFilters" filterValues={safeFilterValues} setFilterValues={setFilterValues}
                    filters={configurations} />
            )}
            {openModal === "NewTaskModal" && (
                <NewTaskModal
                    isOpen={openModal === "NewTaskModal"}
                    closeModal={() => toggleModal("")}
                    boardId={boardId}
                    filterValues={safeFilterValues}
                />
            )}
            {openModal === "NewBoardModal" && (
                <NewBoardModal
                    isOpen={openModal === "NewBoardModal"}
                    closeModal={() => toggleModal("")}
                />
            )}

            <Col xs={6} className="px-0">
                <span className="driver-search-filter">
                    <Input type="text" name="searchTerm" placeholder="Search" onChange={(e) => updateSearchTerm(e.target.value)} />
                    <i className="fa fa-lg fa-search search-icon" aria-hidden="true"></i>
                </span>
            </Col>
            <Col xs={6} className="text-right mt-2 px-0">
                <div className="task-management-header-actions">
                    {hasPermissions?.hasPermissions?.includes("add_taskrabbit") && <>
                        <a className="driver-list-button task-management-action-link" onClick={() => toggleModal("NewTaskModal")}>
                            New Task | <i className="fa fa-plus-square" aria-hidden="true"></i>
                        </a>
                    </>}
                    {showNewBoardButton && hasPermissions?.hasPermissions?.includes("add_board") && <>
                        <a className="driver-list-button task-management-action-link" onClick={() => toggleModal("NewBoardModal")}>
                            New Board | <i className="fa fa-plus-square" aria-hidden="true"></i>
                        </a>
                    </>}

                    {viewType !== "kanban" && <>
                        <TableColumnPreferenceControl
                            tableId={tableId}
                            defaultColumnConfig={defaultColumnConfig}
                            columnConfig={columnConfig}
                            setColumnConfig={setColumnConfig}
                            buttonLabel="Preference"
                        />
                    </>
                    }
                    <a id="viewFilters" className="driver-list-button task-management-action-link" onClick={() => toggleModal("viewFilters")}>
                        Apply Filters | <i className="fa fa-filter" aria-hidden="true"></i> {Object.keys(safeFilterValues).filter(key => safeFilterValues[key] !== null && configurations && configurations.find(setting => setting.name === key) && configurations.find(setting => setting.name === key).showFilter).length}
                    </a>
                </div>
            </Col>
            <Col xs={12} className="mt-1 mb-4 px-0">
                <div className="task-management-bottom-row">
                    <div className="task-management-applied-filters">
                        {Object.keys(safeFilterValues).filter(key => safeFilterValues[key] !== null && configurations && configurations.find(setting => setting.name === key) && configurations.find(setting => setting.name === key).showFilter).map(key =>
                            <span className="search-filter-preview" key={key}>
                                <span>
                                    <i className="fa fa-times-circle" onClick={() => removeFilter(configurations.find(setting => setting.name === key).name)}></i>&nbsp;&nbsp;
                                    {configurations.find(setting => setting.name === key).title}
                                </span>
                                {getValueByFilterType(configurations.find(setting => setting.name === key), safeFilterValues[key])}
                            </span>
                        )}
                    </div>
                    {onViewTypeChange && (
                        <div className="task-management-view-toggle-row">
                            <div className="task-management-view-toggle" role="group" aria-label="Task View Type">
                                <button
                                    type="button"
                                    className={`task-management-view-toggle-btn ${viewType === "list" ? "active" : ""}`}
                                    onClick={() => onViewTypeChange("list")}
                                    title="List View"
                                >
                                    <i className="fa fa-list" aria-hidden="true" />
                                </button>
                                <button
                                    type="button"
                                    className={`task-management-view-toggle-btn ${viewType === "kanban" ? "active" : ""}`}
                                    onClick={() => onViewTypeChange("kanban")}
                                    title="Kanban View"
                                >
                                    <i className="fa fa-columns" aria-hidden="true" />
                                </button>
                            </div>
                            {viewType === "kanban" && boardId && onGroupByChange && (
                                <KanbanSettingsButton boardId={boardId} groupBy={groupBy} onGroupByChange={onGroupByChange} />
                            )}
                        </div>
                    )}
                </div>
            </Col>
        </Row>
    )
}

export default (withApolloClient(TaskManagementListHeader))
